import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AdminShell } from "@/components/admin/AdminShell";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, downloadBlob } from "@/lib/api";
import { toast } from "sonner";
import { LogContactDialog } from "@/pages/admin/LogContactDialog";
import { CustomersTable } from "@/pages/admin/customers/CustomersTable";
import { CustomerDetailDrawer } from "@/pages/admin/customers/CustomerDetailDrawer";
import { CustomerLedgerDialog } from "@/pages/admin/customers/CustomerLedgerDialog";
import { DeleteCustomerDialog } from "@/pages/admin/customers/DeleteCustomerDialog";
import { EMPTY_ADDR, TIERS } from "@/pages/admin/customers/customerConstants";
import { hasAdminSession } from "@/lib/session";

export default function Customers() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_FILTERS = ["all", "pending", "approved", "rejected"];
  const initialFilter = VALID_FILTERS.includes(searchParams.get("filter")) ? searchParams.get("filter") : "all";
  const [shops, setShops] = useState([]);
  const [filter, setFilter] = useState(initialFilter);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [tier, setTier] = useState("Standard");
  const [discountPct, setDiscountPct] = useState(0);
  const [purchases, setPurchases] = useState(null);
  const [logOpen, setLogOpen] = useState(false);
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [ledger, setLedger] = useState(null);
  const [staff, setStaff] = useState(null);
  const [deleteFor, setDeleteFor] = useState(null);
  const [billing, setBilling] = useState({
    gstin: "", state: "", billing_address: EMPTY_ADDR, shipping_address: { ...EMPTY_ADDR, same_as_billing: true },
    ship_same_as_billing: true, payment_terms_days: "",
  });

  const load = async (status = filter) => {
    const { data } = await api.get("/crm/shops", { params: status !== "all" ? { status } : {} });
    setShops(data);
  };
  useEffect(() => {
    if (!hasAdminSession()) { navigate("/admin/login"); return; }
    load("all");
    api.get("/crm/me").then(({ data }) => setStaff(data)).catch(() => {});
    // eslint-disable-next-line
  }, []);
  useEffect(() => { load(filter); /* eslint-disable-next-line */ }, [filter]);
  // sync filter from URL (dashboard deep-links, e.g. ?filter=pending)
  useEffect(() => {
    const f = searchParams.get("filter");
    if (VALID_FILTERS.includes(f) && f !== filter) setFilter(f);
    // eslint-disable-next-line
  }, [searchParams]);
  const changeFilter = (f) => { setFilter(f); setSearchParams(f === "all" ? {} : { filter: f }, { replace: true }); };

  const updateStatus = async (id, status) => {
    await api.put(`/crm/shops/${id}/status`, { status });
    toast.success(`Shop ${status}`);
    load();
    if (selected?.id === id) setSelected({ ...selected, status });
  };
  const openDetail = async (shop) => {
    const { data } = await api.get(`/crm/shops/${shop.id}`);
    setSelected(data); setNotes(data.notes || "");
    setTier(data.tier || "Standard"); setDiscountPct(data.discount_pct || 0);
    setBilling({
      gstin: data.gstin || "", state: data.state || "",
      billing_address: { ...EMPTY_ADDR, ...(data.billing_address || {}) },
      shipping_address: { ...EMPTY_ADDR, ...(data.shipping_address || {}) },
      ship_same_as_billing: data.shipping_address?.same_as_billing !== false,
      payment_terms_days: data.payment_terms_days ?? "",
    });
    setPurchases(null);
    api.get(`/crm/customers/${shop.id}/purchases`).then(({ data }) => setPurchases(data)).catch(() => setPurchases({ orders: [], total_gold_g: 0 }));
  };
  const saveNotes = async () => {
    await api.put(`/crm/shops/${selected.id}/notes`, { notes });
    toast.success("Notes saved"); load();
  };
  const saveDiscount = async () => {
    await api.put(`/crm/shops/${selected.id}/discount`, { discount_pct: Number(discountPct), tier });
    toast.success(`Pricing tier set: ${tier} (${discountPct}% off)`);
    setSelected({ ...selected, tier, discount_pct: Number(discountPct) });
    load();
  };
  const onTierChange = (t) => { setTier(t); if (t !== "Custom") setDiscountPct(TIERS[t]); };

  const saveBilling = async () => {
    try {
      await api.put(`/crm/shops/${selected.id}/billing`, {
        gstin: billing.gstin, state: billing.state,
        billing_address: billing.billing_address,
        shipping_address: billing.ship_same_as_billing ? billing.billing_address : billing.shipping_address,
        ship_same_as_billing: billing.ship_same_as_billing,
        payment_terms_days: billing.payment_terms_days === "" ? null : Number(billing.payment_terms_days),
      });
      toast.success("Billing & tax details saved");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not save billing details"); }
  };
  const updBillAddr = (field, value) => setBilling((b) => ({ ...b, billing_address: { ...b.billing_address, [field]: value } }));
  const updShipAddr = (field, value) => setBilling((b) => ({ ...b, shipping_address: { ...b.shipping_address, [field]: value } }));

  const linkLead = async (shop) => {
    try {
      await api.post(`/crm/leads/${shop.matched_lead_id}/convert`, null, { params: { shop_id: shop.id } });
      toast.success(`Linked lead "${shop.matched_lead_company}" to ${shop.shop_name}`);
      load();
      if (selected?.id === shop.id) setSelected({ ...selected, matched_lead_id: null, matched_lead_company: null });
    } catch (err) { toast.error(err.response?.data?.detail || "Could not link lead"); }
  };

  const openLedger = async (shop) => {
    setLedgerOpen(true);
    setLedger(null);
    try { const { data } = await api.get(`/crm/shops/${shop.id}/ledger`); setLedger(data); }
    catch { setLedger({ entries: [], total_debit: 0, total_credit: 0, closing_balance: 0 }); }
  };
  const downloadLedger = (shop) => {
    downloadBlob(`/crm/shops/${shop.id}/ledger.pdf`, `Ledger-${shop.shop_name}.pdf`).catch(() => toast.error("Download failed"));
  };

  const doDelete = async () => {
    if (!deleteFor) return;
    try {
      await api.delete(`/crm/shops/${deleteFor.id}`);
      toast.success("Customer deleted");
      setDeleteFor(null);
      if (selected?.id === deleteFor.id) setSelected(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not delete customer"); }
  };

  return (
    <AdminShell title="Customer Approval">
      <Tabs value={filter} onValueChange={changeFilter} className="mb-4">
        <TabsList>
          <TabsTrigger data-testid="customers-tab-all" value="all">All</TabsTrigger>
          <TabsTrigger data-testid="customers-tab-pending" value="pending">Pending</TabsTrigger>
          <TabsTrigger data-testid="customers-tab-approved" value="approved">Approved</TabsTrigger>
          <TabsTrigger data-testid="customers-tab-rejected" value="rejected">Rejected</TabsTrigger>
        </TabsList>
      </Tabs>

      <CustomersTable shops={shops} staff={staff} onOpenDetail={openDetail} onUpdateStatus={updateStatus} onLinkLead={linkLead} onDelete={setDeleteFor} />

      <CustomerDetailDrawer
        selected={selected} onClose={() => setSelected(null)} purchases={purchases} staff={staff}
        onUpdateStatus={updateStatus} onLinkLead={linkLead} onOpenLedger={openLedger}
        onLogContact={() => setLogOpen(true)} onDelete={setDeleteFor}
        tier={tier} onTierChange={onTierChange} discountPct={discountPct} setDiscountPct={setDiscountPct} onSaveDiscount={saveDiscount}
        billing={billing} setBilling={setBilling} onUpdBillAddr={updBillAddr} onUpdShipAddr={updShipAddr} onSaveBilling={saveBilling}
        notes={notes} setNotes={setNotes} onSaveNotes={saveNotes}
      />

      <LogContactDialog open={logOpen} onOpenChange={setLogOpen}
        entity={selected ? { type: "customer", id: selected.id, name: selected.shop_name } : null}
        stages={["approved", "blocked"]}
        onSaved={() => { if (selected) openDetail(selected); load(); }} />

      <CustomerLedgerDialog open={ledgerOpen} onClose={() => setLedgerOpen(false)} shop={selected} ledger={ledger} onDownload={downloadLedger} />

      <DeleteCustomerDialog deleteFor={deleteFor} onClose={() => setDeleteFor(null)} onConfirm={doDelete} />
    </AdminShell>
  );
}
